import { Fragment, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Icon, Info, MapPin, X } from '../../ui/Icon';
import Spinner from '../../ui/Spinner';
import { useDateLocale } from '../../../utils/dateLocale';
import { useTripHourlyWeather } from '../../../hooks/useTripHourlyWeather';
import { DayWeatherChip } from './DayWeatherChip';
import { DayHoursTrace } from './DayHoursTrace';
import type { DayWeather } from '../../../api/weather.api';
import type { ItineraryDay } from '../../../types';

interface Props {
  open: boolean;
  onClose: () => void;
  tripId: string;
  /** `tripWeatherKey(trip)`; a new one refetches the trace. */
  placesKey: string;
  days: ItineraryDay[];
  /** The day summaries already loaded by the itinerary — reused, not refetched. */
  weather: Record<string, DayWeather>;
  /** Day to scroll to when the panel opens; the chip the user clicked. */
  focusDate: string | null;
}

/**
 * The trip's weather, hour by hour, in a slide-over.
 *
 * A slide-over rather than a second docked rail: the right edge of the trip
 * layout already belongs to `AiDockPanel`, which expands by pushing content.
 * Two of those would leave the itinerary with no width on desktop and would not
 * fit at all on a phone. Floating over the content costs nothing from either.
 */
export function HourlyWeatherPanel({ open, onClose, tripId, placesKey, days, weather, focusDate }: Props) {
  const { t } = useTranslation();
  const locale = useDateLocale();
  const { byDate, loading } = useTripHourlyWeather(tripId, placesKey, open);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Scroll after the trace has rendered: the sections have very different
  // heights, so an offset measured before it lands on the wrong day.
  useEffect(() => {
    if (!open || !focusDate) return;
    const target = scroller.current?.querySelector(`[data-date="${focusDate}"]`);
    target?.scrollIntoView({ block: 'start' });
  }, [open, focusDate, byDate]);

  if (!open) return null;

  // The explanation is a property of the trip's distance, not of each day: on a
  // trip that starts more than ~3 days out every single day is 6-hourly, and
  // repeating the sentence under all ten of them is noise, not honesty. Said
  // once here; per day it is the `6 h` badge and the fact that the readings are
  // drawn apart instead of joined.
  const anyBlocks = Object.values(byDate).some(d => d.granularity === 'sixHour');

  // Same reasoning for the tail of the trip: the days past the forecast are
  // contiguous, so the explanation is a divider once, not a footnote under each
  // of them. What stays per day is the chip's own "typical for {month}" label —
  // that is ADR-0006's requirement and it is a label, not a repeated paragraph.
  const firstBeyond = days.find(d => !byDate[d.date.slice(0, 10)])?.id;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end animate-fade-in">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <aside
        role="dialog"
        aria-label={t('itinerary.weather.panel.title')}
        className="relative z-10 w-full max-w-sm h-full flex flex-col bg-slate-50 dark:bg-slate-950 border-l border-slate-200 dark:border-slate-800 shadow-2xl animate-slide-up"
      >
        <header className="shrink-0 px-5 py-4 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="font-display font-semibold text-base text-slate-900 dark:text-slate-100">
                {t('itinerary.weather.panel.title')}
              </h2>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                {t('itinerary.weather.panel.subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="btn-icon w-7 h-7 shrink-0 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              aria-label={t('common.close')}
            >
              <Icon as={X} size={16} />
            </button>
          </div>

          {/* Kept in plain sight rather than behind a toggle: it is the answer to
              "why are there only four readings?", and a reader who has to click
              to find it asks the question anyway. Lightened instead — a note
              with a mark, not a slab of grey. */}
          {(anyBlocks || firstBeyond) && (
            <div className="mt-3 flex gap-2 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/30 px-3 py-2">
              <Icon as={Info} size={13} className="mt-px shrink-0 text-slate-400 dark:text-slate-500" />
              <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                {anyBlocks && t('itinerary.weather.panel.blocksLegend')}
                {anyBlocks && firstBeyond && ' '}
                {firstBeyond && t('itinerary.weather.panel.noHourly')}
              </p>
            </div>
          )}
        </header>

        <div ref={scroller} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {loading && (
            <div className="flex justify-center py-10"><Spinner /></div>
          )}

          {!loading && days.map(day => {
            const date = day.date.slice(0, 10);
            const hours = byDate[date];
            const summary = weather[date];

            return (
              // A fragment, not a `display: contents` wrapper: a box-less
              // element still counts as the flex child, and `space-y` would put
              // its margin there — where it does nothing, leaving every card
              // glued to the next.
              <Fragment key={day.id}>
              {day.id === firstBeyond && (
                <div className="flex items-center gap-2 pt-1 pb-1">
                  <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
                  <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
                    {t('itinerary.weather.panel.beyondForecast')}
                  </span>
                  <span className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />
                </div>
              )}
              <section
                data-date={date}
                /* The day the user clicked keeps a ring: the panel scrolls to it,
                   and after the scroll nothing else says which one it stopped on. */
                className={`card p-4 scroll-mt-4${
                  date === focusDate ? ' ring-1 ring-teal-500/40 dark:ring-teal-400/30 shadow-card-md' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2 pb-2.5 mb-2.5 border-b border-slate-100 dark:border-slate-800/80">
                  <div className="min-w-0">
                    <h3 className="font-display text-[15px] font-semibold text-slate-800 dark:text-slate-200 truncate first-letter:uppercase">
                      {format(parseISO(date), 'EEE, d MMM', { locale })}
                    </h3>
                    {/* Which stop the numbers below are about. A day can cross
                        200 km; without this the temperature has no subject. */}
                    {summary?.place && (
                      <p
                        title={t(summary.place.from === 'trip' ? 'itinerary.weather.atTrip' : 'itinerary.weather.at', { place: summary.place.name })}
                        className="mt-1 flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500 min-w-0"
                      >
                        <Icon as={MapPin} size={10} className="shrink-0" />
                        <span className="truncate">
                          {summary.place.name}
                          {summary.place.from === 'trip' && ` · ${t('itinerary.weather.tripDestination')}`}
                        </span>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {hours?.granularity === 'sixHour' && (
                      <span
                        title={t('itinerary.weather.panel.blocksLegend')}
                        className="font-mono text-[9px] tracking-[0.08em] rounded-md border border-slate-200 dark:border-slate-700 px-1.5 py-0.5 text-slate-400 dark:text-slate-500"
                      >
                        6 h
                      </span>
                    )}
                    <span className="font-mono text-[9px] uppercase tracking-[0.1em] rounded-md bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-slate-500 dark:text-slate-400">
                      {t('itinerary.dayLabel', { n: day.dayNumber })}
                    </span>
                  </div>
                </div>

                {hours ? (
                  <DayHoursTrace day={hours} />
                ) : (
                  /* ADR-0006: past the forecast horizon the only source is a
                     30-year monthly mean. It is shown as what it is — the chip
                     carries "typical for {month}" — and never stretched into a
                     curve to make this list look uniform. */
                  summary
                    ? <DayWeatherChip weather={summary} variant="full" locale={locale} />
                    : <p className="text-[11px] text-slate-400">{t('itinerary.weather.panel.noData')}</p>
                )}
              </section>
              </Fragment>
            );
          })}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
